/**
 * Idle State Manager
 * 
 * Detects idle state and manages resource cleanup
 * Requirements: 19.5
 */

export interface IdleStateConfig {
  idleTimeout: number; // milliseconds
  onIdle: () => void;
  onActive: () => void;
}

export class IdleStateManager {
  private idleTimeout: number;
  private idleTimer: number | null = null;
  private isIdle = false;
  private onIdleCallback: () => void;
  private onActiveCallback: () => void;
  private events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  constructor(config: IdleStateConfig) {
    this.idleTimeout = config.idleTimeout;
    this.onIdleCallback = config.onIdle;
    this.onActiveCallback = config.onActive;
  }

  /**
   * Start monitoring for idle state
   */
  start(): void {
    this.resetIdleTimer();
    this.attachEventListeners();
  }

  /**
   * Stop monitoring for idle state
   */
  stop(): void {
    this.detachEventListeners();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  /**
   * Check if currently idle
   */
  isCurrentlyIdle(): boolean {
    return this.isIdle;
  }

  /**
   * Reset the idle timer
   */
  private resetIdleTimer = (): void => {
    // If was idle, trigger active callback
    if (this.isIdle) {
      this.isIdle = false;
      this.onActiveCallback();
    }

    // Clear existing timer
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    // Set new timer
    this.idleTimer = window.setTimeout(() => {
      this.isIdle = true;
      this.onIdleCallback();
    }, this.idleTimeout);
  };

  /**
   * Attach event listeners for user activity
   */
  private attachEventListeners(): void {
    this.events.forEach((event) => {
      window.addEventListener(event, this.resetIdleTimer, true);
    });
  }

  /**
   * Detach event listeners
   */
  private detachEventListeners(): void {
    this.events.forEach((event) => {
      window.removeEventListener(event, this.resetIdleTimer, true);
    });
  }
}

/**
 * Resource Cleanup Manager
 * 
 * Manages cleanup of resources during idle state
 */
export class ResourceCleanupManager {
  private cleanupCallbacks: Array<() => void> = [];

  /**
   * Register a cleanup callback
   */
  registerCleanup(callback: () => void): void {
    this.cleanupCallbacks.push(callback);
  }

  /**
   * Unregister a cleanup callback
   */
  unregisterCleanup(callback: () => void): void {
    const index = this.cleanupCallbacks.indexOf(callback);
    if (index > -1) {
      this.cleanupCallbacks.splice(index, 1);
    }
  }

  /**
   * Execute all cleanup callbacks
   */
  cleanup(): void {
    console.log('Executing resource cleanup for idle state');
    this.cleanupCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('Error during resource cleanup:', error);
      }
    });
  }

  /**
   * Clear all registered callbacks
   */
  clear(): void {
    this.cleanupCallbacks = [];
  }
}

// Export singleton instances
export const resourceCleanupManager = new ResourceCleanupManager();
